import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import { ALLOCATION_EPSILON } from './allocationConstants';
import SportAllocationModal from './SportAllocationModal';

import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';

import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import ExclamationTriangleIcon from 'Assets/icons/exclamation-triangle.svg';
import TrashIcon from 'Assets/icons/trash.svg';

/**
 * How many allocation chips render inline before the remainder collapse into a
 * `+N` badge.
 *
 * IB-875 (testing feedback 2026-07-28): this cell used to wrap, so a line split
 * across three events stood three text-rows tall and a 182-line expenditure
 * table became unscannable. A row must now stay one line high however many
 * events it is allocated to, so the visible chip count is *capped* rather than
 * measured — a fixed cap needs no per-row DOM measurement and cannot reflow on
 * first paint. Widen the column and this is the one number to retune.
 */
const VISIBLE_CHIP_LIMIT = 1;

/**
 * EventTagCell — renders sport allocation badges on a budget line table row.
 *
 * Always occupies a single line: the chips never wrap, long event names clip
 * with an ellipsis, and everything past VISIBLE_CHIP_LIMIT moves into a `+N`
 * badge whose popover renders in a portal (so it costs no row height).
 *
 * Props:
 *   line        — { id, type: 'expense'|'revenue'|'position', allocations: [{sport_id, sport_name, percentage}] }
 *   storeUrl    — the URL template for POST /budget-lines/{type}/{id}/sport-allocations
 *   sports      — [{id, name}] — the event's sports list for the picker
 *   onUpdated   — callback(allocations) invoked after a successful save
 *   readonly    — boolean; when true, renders badges only (no edit button)
 *   allowClear  — boolean; when true, renders a trash button to clear all tags
 *   isSummary   — boolean; when true, the badges summarise allocations owned elsewhere
 *                 rather than describing this line's own split, so a sub-100% total is
 *                 normal and the under-allocated warning is suppressed (IB-878: a pack's
 *                 pack-level cell summarises its per-day tags)
 *   note        — string; caller-supplied aside about where the tags are owned. Rendered
 *                 as an inline info marker with the text on hover, never as a second
 *                 line, so the row keeps its single-line height (IB-875). Must be
 *                 trusted, non-user-supplied text: it reaches TextTooltip, which renders
 *                 a string child through dangerouslySetInnerHTML, and i18next here runs
 *                 with escapeValue: false. Pass a translation literal, never line data.
 */
export default function EventTagCell({
    line,
    storeUrl,
    sports,
    onUpdated,
    readonly = false,
    venueId = null,
    allowClear = false,
    isSummary = false,
    note = null,
}) {
    const [showModal, setShowModal] = useState(false);
    const [allocations, setAllocations] = useState(line?.allocations ?? []);
    const [clearing, setClearing] = useState(false);

    // `allocations` is seeded from props once above, with nothing re-syncing it
    // when `line.allocations` changes underneath — this cell has no remount
    // trigger (RENodeRow's `key` is path-stable across a refetch). PackLineRow
    // recomputes its day-tag summary and passes it in as `line.allocations` on
    // every render, so without this the pack-level summary cell never refreshes
    // after a day tag changes elsewhere (IB-878 review).
    useEffect(() => {
        setAllocations(line?.allocations ?? []);
    }, [line?.allocations]);

    const confirm = useConfirmationModal();

    // The modal hides itself after a successful save; onClose unmounts it once
    // the flowbite hide transition has finished.
    const handleSaved = (updated) => {
        setAllocations(updated);
        onUpdated && onUpdated(updated);
    };

    const performClear = () => {
        setClearing(true);
        // DELETE to the same collection URL as the POST store endpoint by design —
        // sport-allocations.destroy-all and sport-allocations.store share the path.
        // Do not split them into separate URL props.
        //
        // When storeUrl is a fully-resolved URL (no placeholders), use it as-is.
        // This supports day-level pack tags where the URL is pre-built per row.
        const url =
            storeUrl.includes('__TYPE__') || storeUrl.includes('__ID__') ? storeUrl.replace('__TYPE__', line.type).replace('__ID__', line.id) : storeUrl;

        return axios
            .delete(url, venueId ? { data: { venue_id: venueId } } : undefined)
            .then(() => {
                setAllocations([]);
                onUpdated && onUpdated([]);
            })
            .catch(() => {
                window.alert($t('Failed to remove event tags. Please try again.'));
            })
            .finally(() => setClearing(false));
    };

    const handleClear = () => {
        if (clearing || allocations.length === 0) return;

        confirm({
            title: $t('Remove event tags'),
            message: $t('Remove all event tags from this line?'),
            onConfirm: performClear,
        });
    };

    if (!line) return null;

    // Verified Requirement #4 (Linear IB-877, 2026-07-24): amber fires when the
    // line's sport allocations sum to less than 100% — including
    // fully-unallocated (never-tagged) lines, so nothing is missed. Also
    // preserves the pre-existing 0%-newly-added-sport signal: rebalanceLineAt
    // VenueOrAddSportAtZero can add a sport at 0% while the REST of the split
    // still sums to 100 (isUnderAllocated alone would miss that; it was
    // previously flagged by `allocations.some(p === 0)`, downgraded here to a
    // grey 0% chip via SportTag but kept in the row-level indicator too).
    const allocatedPercentage = allocations.reduce((sum, a) => sum + Number(a.percentage || 0), 0);
    const isUnderAllocated = allocatedPercentage < 100 - ALLOCATION_EPSILON;
    const hasZeroAllocation = allocations.some((a) => Number(a.percentage) === 0);
    // Both warnings are a call to action ("review and reallocate"), so they are
    // suppressed only where the shortfall is not a fault to act on: a summary
    // cell aggregates a split owned elsewhere (IB-878, decision 2 — a pack's
    // pack-level cell summarises its per-day tags), where sub-100% is normal.
    // Deliberately NOT keyed on `readonly`: ticketing and schedule-derived tags
    // are also readonly, and their under-allocation warning predates this and
    // still carries information.
    const showAmberWarning = !isSummary && (isUnderAllocated || hasZeroAllocation);

    // Ranked by share, descending, so the chip that survives the cap is the
    // meaningful one. The server order is arbitrary for this purpose: expenses and
    // revenues read `sportAllocations` with no `orderBy`, and a pack's day-tag
    // summary comes out in ascending sport_id — so an unsorted cap could show
    // "Archery 10%" and hide "Football 90%". Display-only; `allocations` itself is
    // left alone, so saving and clearing still round-trip the server's own order.
    //
    // 0%-allocated entries are ranked ahead of everything else, share-descending
    // ties aside: a 0% split sorts last under a pure share-descending compare (it
    // is, by definition, the smallest possible share), which would put the
    // "newly added, review allocation" signal (`hasZeroAllocation` above) behind
    // the `+N` badge on every multi-event line that has one — the row-level amber
    // warning would still fire, but *which* event needs attention would become
    // hover-only. Surfacing it as the visible chip keeps that signal on-screen
    // without a hover, same as before this cap existed.
    const rankedAllocations = [...allocations].sort((a, b) => {
        const aIsZero = Number(a.percentage) === 0;
        const bIsZero = Number(b.percentage) === 0;
        if (aIsZero !== bIsZero) return aIsZero ? -1 : 1;
        return Number(b.percentage || 0) - Number(a.percentage || 0);
    });
    const visibleAllocations = rankedAllocations.slice(0, VISIBLE_CHIP_LIMIT);
    const hiddenCount = rankedAllocations.length - visibleAllocations.length;

    return (
        // Two classes carry the IB-875 acceptance here: `tw-flex-nowrap` stops the
        // chips wrapping onto a second line, and `tw-min-h-6` floors the cell at one
        // chip-height so a row measures the same whether it carries no allocation,
        // one, or six. Every sibling is `tw-shrink-0` — only the chip's name gives
        // way under pressure. A floor rather than a fixed height: a chip is exactly
        // 1.5rem today, so both measure identically, but a future border or taller
        // icon would overflow a fixed height and paint over the next row.
        //
        // `tw-min-w-0` on *this* div is a no-op: it's a flex item inside a `<td>` in
        // a `tw-table-fixed` table, so its width comes from the column, never from
        // content — min-width is never the binding constraint here. Left in place as
        // harmless, but the truncation that actually matters is `tw-min-w-0` on the
        // chip span itself, in `SportTag` below — debug there, not here.
        <div className="tw-flex tw-min-h-6 tw-flex-nowrap tw-items-center tw-gap-1 tw-min-w-0">
            {showAmberWarning && (
                <span
                    className="tw-cursor-help tw-shrink-0"
                    title={
                        isUnderAllocated
                            ? $t('This line is only {{percent}}% allocated — the remainder is unallocated. Review and reallocate.', {
                                  percent: Number(allocatedPercentage.toFixed(2)),
                              })
                            : $t('One or more sports on this line are allocated 0% — newly added, review allocation.')
                    }
                    aria-label={$t('Line needs review')}
                >
                    <ExclamationTriangleIcon className="tw-w-3.5 tw-h-3.5 tw-text-yellow-400" />
                </span>
            )}
            {visibleAllocations.map((a, i) => (
                // The percentage is dropped while a `+N` is present: the Events column is
                // 160px (`tw-w-40`) in a fixed-layout table, and name + percentage + `+N` +
                // Edit + clear simply do not fit — the name was being squeezed to 20px, and
                // to 2px once the warning icon appeared, so the cell read "…33.33%" with no
                // event at all. Nothing is lost: the `+N` popover lists every event with its
                // share, and the chip's own title still carries name + percentage. A lone
                // allocation keeps its percentage inline, as there is room for both.
                //
                // Gated on `rankedAllocations.length === 1` rather than `hiddenCount === 0`
                // so `VISIBLE_CHIP_LIMIT` keeps meaning only "how many chips render" — its
                // own docblock invites retuning it, and a `hiddenCount`-based gate would
                // silently reintroduce the squeeze this fixes if the cap were ever raised
                // (e.g. to 2, two chips-with-percentages would no longer fit the column).
                <SportTag key={a.sport_id ?? i} allocation={a} showPercentage={rankedAllocations.length === 1} />
            ))}

            {hiddenCount > 0 && <HiddenAllocationsBadge allocations={rankedAllocations} hiddenCount={hiddenCount} />}

            {note && (
                // 'click' deliberately omitted: `mouseenter` already opens the popover, so a
                // following click only ever hits `toggleTooltip` and immediately closes it —
                // verified against react-popper-tooltip's source (mouseenter/mouseleave for
                // 'hover', a separate click-toggle listener for 'click' — the two conflict
                // rather than combine). Codebase precedent is ['hover', 'focus'] throughout
                // (e.g. MasterplanTable.js:278) — nothing else pairs hover with click.
                <Tooltip tooltipType="text" placement="top" trigger={['hover', 'focus']} tooltipContent={note}>
                    <button type="button" className="tw-inline-flex tw-shrink-0 tw-cursor-help tw-items-center" aria-label={note}>
                        <CircleInfoIcon className="tw-w-3.5 tw-h-3.5 tw-text-charcoal-50" />
                    </button>
                </Tooltip>
            )}

            {!readonly && (
                <TextButton className="tw-shrink-0" variant="primary" onClick={() => setShowModal(true)} title={$t('Edit event tags')}>
                    {allocations.length > 0 ? $t('Edit') : $t('+ Tag')}
                </TextButton>
            )}

            {!readonly && allowClear && allocations.length > 0 && (
                <TextButton
                    className="tw-shrink-0"
                    variant="danger"
                    onClick={handleClear}
                    disabled={clearing}
                    title={$t('Remove all event tags')}
                    aria-label={$t('Remove all event tags')}
                >
                    <TrashIcon className="tw-w-3 tw-h-3" />
                </TextButton>
            )}

            {showModal && (
                <SportAllocationModal
                    line={line}
                    storeUrl={storeUrl}
                    sports={sports}
                    currentAllocations={allocations}
                    onSaved={handleSaved}
                    onClose={() => setShowModal(false)}
                    venueId={venueId}
                />
            )}
        </div>
    );
}

function SportTag({ allocation, showPercentage = true }) {
    const isZero = Number(allocation.percentage) === 0;

    return (
        <span
            className={classNames(
                'tw-inline-flex tw-items-center tw-min-w-0 tw-max-w-[7.5rem] tw-rounded-lg tw-px-1.5 tw-py-1 tw-text-xs tw-font-medium',
                isZero ? 'tw-bg-charcoal-15 tw-text-charcoal-75' : 'tw-bg-blue tw-text-white',
            )}
            title={isZero ? `${allocation.sport_name}: 0% — ${$t('newly added, review allocation')}` : `${allocation.sport_name}: ${allocation.percentage}%`}
        >
            {/* A long event name clips with an ellipsis rather than wrapping the row
                onto a second line (IB-875) — the full name stays on the title tooltip.
                The percentage never truncates: it is the part being scanned for. */}
            <span className="tw-truncate">{allocation.sport_name}</span>
            {showPercentage && allocation.percentage != null && allocation.percentage < 100 && (
                <span className="tw-opacity-75 tw-ms-0.5 tw-shrink-0">{allocation.percentage}%</span>
            )}
        </span>
    );
}

/**
 * `+N` overflow badge (IB-875). Reveals the line's *full* split — including the
 * allocation already visible as a chip — in a popover that `Tooltip` renders
 * through a portal, so the hidden events cost the row no height.
 */
function HiddenAllocationsBadge({ allocations, hiddenCount }) {
    // The accessible name carries both numbers. Visible text is "+2" (hidden
    // count only); the popover it opens lists the *whole* split, chip included,
    // so a label built from either number alone hands a sighted user and a
    // screen-reader user different numbers for the same control — WCAG 2.5.3
    // (Label in Name) wants the accessible name to contain the visible label.
    // "more events" (not "more") sidesteps the ungrammatical "1 more events" —
    // unreachable anyway, since the badge only renders when `hiddenCount > 0`
    // and `VISIBLE_CHIP_LIMIT` is 1, so the total is always >= 2.
    const label = $t('+{{hidden}} more — show all {{total}} events', { hidden: hiddenCount, total: allocations.length });

    return (
        <Tooltip
            tooltipType="content"
            placement="top"
            // 'click' deliberately omitted here too — same conflict as the `note` marker
            // above, and it matters more on this badge: the popover is now the only home
            // for the hidden shares, so a click that closes it rather than opening it is
            // a bigger loss.
            trigger={['hover', 'focus']}
            backgroundClass="tw-bg-charcoal-75"
            tooltipContent={
                <ul className="tw-flex tw-flex-col tw-gap-y-1 tw-text-xs tw-text-white">
                    {allocations.map((a, i) => (
                        <li key={a.sport_id ?? i} className="tw-flex tw-justify-between tw-gap-x-3 tw-text-nowrap">
                            <span>{a.sport_name}</span>
                            {a.percentage != null && <span className="tw-opacity-75">{a.percentage}%</span>}
                        </li>
                    ))}
                </ul>
            }
        >
            {/* A real button, not a span with tabIndex: it is genuinely activatable and
                carries its own accessible name, where `aria-label` on a role-less span is
                discarded by conforming screen readers. `dir="ltr"` keeps "+2" from being
                laid out as "2+" by RTL bidi — it is the one non-translatable string here. */}
            <button
                type="button"
                dir="ltr"
                className="tw-inline-flex tw-shrink-0 tw-cursor-help tw-items-center tw-rounded-lg tw-bg-charcoal-15 tw-px-1.5 tw-py-1 tw-text-xs tw-font-medium tw-text-charcoal-75"
                aria-label={label}
            >
                +{hiddenCount}
            </button>
        </Tooltip>
    );
}
